import { AccessTime, CheckCircle, WarningAmberRounded } from '@mui/icons-material';
import { Box, Grid, Typography, Collapse, Slide, Grow } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Howl } from 'howler';
import delayedOrder from '../assets/sounds/delayedOrder.wav';
import { API } from '../utils/Api';

const getNewOrderSecondsLeft = (order) => {
    if (!order?.order?.createdAt) return 0;

    const createdTime = new Date(order.order.createdAt).getTime();
    const now = Date.now();

    const elapsedSeconds = Math.floor((now - createdTime) / 1000);
    return Math.max(0, API.NEW_ORDER_COUNTDOWN_SECONDS - elapsedSeconds);
};

const getReducedReadyTime = (time) => {
    if (!time) return '';

    const [hours, minutes] = time.split(':').map(Number);

    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes - API.BUFFER_TIME);
    const newHours = date.getHours().toString().padStart(2, '0');
    const newMinutes = date.getMinutes().toString().padStart(2, '0');

    return `${newHours}:${newMinutes}`;
};

const formatMinutesSeconds = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

function playSound() {
    const sound = new Howl({
        src: [delayedOrder],
        volume: 1.0,
    });
    sound.play();
}

const isOrderDelayed = (order) => {
    const delayStart = getOrderDelayStartTime(order);
    if (!delayStart) return false;

    return new Date() > delayStart;
};

const getOrderDelayStartTime = (order) => {
    if (!order?.order?.readyTime || !order?.order?.createdAt) return null;

    const reducedTime = getReducedReadyTime(order.order.readyTime);
    const [h, m] = reducedTime.split(':').map(Number);

    const delayStart = new Date(order.order.createdAt);
    delayStart.setHours(h);
    delayStart.setMinutes(m);
    delayStart.setSeconds(0);
    delayStart.setMilliseconds(0);

    return delayStart;
};

const getOrderReadyTime = (order) => {
    if (!order?.order?.readyTime || !order?.order?.createdAt) return null;

    const [h, m] = order.order.readyTime.split(':').map(Number);

    const readyTime = new Date(order.order.createdAt);
    readyTime.setHours(h);
    readyTime.setMinutes(m);
    readyTime.setSeconds(0);
    readyTime.setMilliseconds(0);

    return readyTime;
};

const getOrderArrivalMeta = (order, now = new Date()) => {
    const delayStart = getOrderDelayStartTime(order); // countdown starts here
    const readyTime = getOrderReadyTime(order); // delayed time starts only after this

    if (!delayStart || !readyTime) {
        return {
            orderArrivalStatus: null,
            earlyArrival: null,
            delayedArrival: null,
        };
    }

    const diffFromDelayStart = Math.floor((now.getTime() - delayStart.getTime()) / 1000);
    const diffFromReadyTime = Math.floor((now.getTime() - readyTime.getTime()) / 1000);

    // Before buffer-hit time
    if (diffFromDelayStart < 0) {
        return {
            orderArrivalStatus: "EARLY",
            earlyArrival: formatMinutesSeconds(Math.abs(diffFromDelayStart)),
            delayedArrival: null,
        };
    }

    // Buffer-hit reached, countdown started, but actual ready time not crossed yet
    if (diffFromDelayStart >= 0 && diffFromReadyTime < 0) {
        return {
            orderArrivalStatus: null,
            earlyArrival: null,
            delayedArrival: null,
        };
    }

    // Actual ready time crossed, now send delayed time
    if (diffFromReadyTime >= 0) {
        return {
            orderArrivalStatus: "DELAYED",
            earlyArrival: null,
            delayedArrival: formatMinutesSeconds(diffFromReadyTime),
        };
    }

    return {
        orderArrivalStatus: null,
        earlyArrival: null,
        delayedArrival: null,
    };
};

function OrderCard({ orders, onOrderClick, onArrivalMetaChange }) {

    const initialExpandedOrderId = orders?.length > 0 ? orders[0]?.order?._id : null;
    const [expandedOrderId, setExpandedOrderId] = useState();
    const [openedOrders, setOpenedOrders] = useState(() => {
        const saved = localStorage.getItem('openedOrders');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [nowTick, setNowTick] = useState(Date.now());

    useEffect(() => {
        localStorage.setItem('openedOrders', JSON.stringify([...openedOrders]));
    }, [openedOrders]);

    const handleCardClick = (orderId) => {
        console.log('Clicked order ID:', orderId);
        // Toggle expansion: if the order is already expanded, collapse it (set to null)
        if (expandedOrderId === orderId) {
            setExpandedOrderId(null);
            onOrderClick(null);  // Pass null to indicate no order is selected
        } else {
            setExpandedOrderId(orderId);
            setOpenedOrders(prev => new Set(prev.add(orderId)));
            onOrderClick(orderId);  // Pass the new order ID
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setNowTick(Date.now());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    return (
        <>
            <Box>
                {orders?.map((order) => {
                    const status = order?.order?.orderStatus;
                    const isFinalStatus =
                        status === 'COMPLETED' || status === 'DELIVERED';
                    const arrivalMeta = getOrderArrivalMeta(order);
                    const newOrderSecondsLeft = getNewOrderSecondsLeft(order);
                    const shouldShowPreparationCountdown =
                        !isFinalStatus &&
                        newOrderSecondsLeft > 0 &&
                        expandedOrderId !== order.order._id;

                    return (
                        <div
                            key={order.order._id}
                            sx={{ backgroundColor: '#F6F6F6', cursor: 'pointer', margin: '10px', borderRadius: '8px' }}
                            onClick={() => {
                                handleCardClick(order?.order?._id);

                                if (onArrivalMetaChange) {
                                    onArrivalMetaChange({
                                        orderId: order?.order?._id,
                                        ...arrivalMeta,
                                    });
                                }
                            }}
                        >
                            {expandedOrderId === order?.order?._id ? (
                                <ExpandedView order={order} />
                            ) : shouldShowPreparationCountdown ? (
                                <NewOrderPreparationCountdown
                                    order={order}
                                    secondsLeft={newOrderSecondsLeft}
                                />
                            ) : !isFinalStatus && isOrderDelayed(order) ? (
                                <DelayedOrderView order={order} />
                            ) : (
                                <CollapsedView
                                    order={order}
                                    hasBeenOpened={openedOrders.has(order.order._id)}
                                />
                            )}
                        </div>
                    );
                })}
            </Box>
        </>
    );
}

const NewOrderPreparationCountdown = ({ order, secondsLeft }) => {
    const totalSeconds = API.NEW_ORDER_COUNTDOWN_SECONDS;
    const progress = (secondsLeft / totalSeconds) * 100;
    const isUrgent = secondsLeft <= 3;

    const getMessage = () => {
        if (secondsLeft <= 1) return "Starting now";
        if (secondsLeft <= 3) return "Preparation starting...";
        return "Start Preparation in";
    };

    return (
        <Grow in timeout={400}>
            <Box
                className="cursor-p"
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    m: 1.5,
                    mt: 2,
                    borderRadius: '18px',
                    px: 2,
                    py: 1.8,
                    color: '#fff',
                    backdropFilter: 'blur(6px)',

                    background: isUrgent
                        ? 'linear-gradient(135deg, #B71C1C, #E53935)'
                        : 'linear-gradient(135deg, #0D47A1, #0288D1)',

                    boxShadow: isUrgent
                        ? '0 10px 28px rgba(211,47,47,0.35)'
                        : '0 10px 24px rgba(2,136,209,0.30)',

                    transition: 'all 0.6s ease',

                    '&:hover': {
                        transform: 'translateY(-2px) scale(1.01)',
                    },

                    animation: isUrgent ? 'pulseSoft 1.2s ease-in-out infinite' : 'none',

                    '@keyframes pulseSoft': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.015)' },
                        '100%': { transform: 'scale(1)' },
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)',
                        transform: 'translateX(-100%)',
                        animation: 'shimmer 2.8s infinite',

                        '@keyframes shimmer': {
                            '0%': { transform: 'translateX(-100%)' },
                            '60%': { transform: 'translateX(100%)' },
                            '100%': { transform: 'translateX(100%)' },
                        },
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '4px',
                        width: `${progress}%`,
                        background: '#fff',
                        opacity: 0.8,
                        borderRadius: '0 10px 10px 0',
                        transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
                    }}
                />

                <Grid container alignItems="center" justifyContent="space-between" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography fontWeight={900} fontSize={15}>
                                New Order
                            </Typography>

                            <Box
                                sx={{
                                    px: 1.2,
                                    py: '2px',
                                    fontSize: 9,
                                    fontWeight: 800,
                                    borderRadius: '12px',
                                    background: 'rgba(255,255,255,0.2)',
                                }}
                            >
                                NEW
                            </Box>
                        </Box>

                        <Typography fontSize={12} mt={0.6} sx={{ opacity: 0.9 }}>
                            #{order?.order?.order_sequence}{order?.order?.order_no}
                        </Typography>

                        <Typography fontSize={11} sx={{ opacity: 0.75 }}>
                            {order?.order?.ordersType} • {order?.order?.orderItemsCount} items
                        </Typography>
                    </Grid>

                    <Grid item xs={3} textAlign="center">
                        <Typography fontSize={10} fontWeight={700} mb={0.5} sx={{ opacity: 0.9 }}>
                            {getMessage()}
                        </Typography>

                        <Box
                            sx={{
                                position: 'relative',
                                width: 56,
                                height: 56,
                                mx: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '50%',
                                    background: `conic-gradient(#fff ${progress * 3.6}deg, rgba(255,255,255,0.2) 0deg)`,
                                    transition: 'all 1s linear',
                                }}
                            />

                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 6,
                                    borderRadius: '50%',
                                    background: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.1)',
                                }}
                            >
                                <Typography
                                    key={secondsLeft}
                                    sx={{
                                        fontSize: 22,
                                        fontWeight: 900,
                                        color: isUrgent ? '#B71C1C' : '#0D47A1',
                                        transition: 'all 0.25s ease',
                                    }}
                                >
                                    {secondsLeft}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={3} textAlign="right">
                        <Typography fontWeight={900} fontSize={15}>
                            ₹ {order?.order?.grandTotal?.toFixed(2)}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        </Grow>
    );
};

const CollapsedView = ({ order, hasBeenOpened }) => {
    const formattedGrandTotal = order?.order?.grandTotal?.toFixed(2);
    const dateString = order?.order?.createdAt;
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;
    const orderStatus = order?.order?.orderStatus;
    const shouldShowNewTag = !hasBeenOpened && orderStatus !== 'DELIVERED' && orderStatus !== 'COMPLETED';

    return (
        <Box className="cursor-p" sx={{ backgroundColor: shouldShowNewTag ? order?.order?.ordersType === "DELIVERY" ? '#DCEDC8' : '#E0F7FA' : '#fff', color: '#000', padding: '13px 17px 13px 17px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)', margin: '10px', marginTop: '18px', borderRadius: '8px' }}>
            <Grid container justifyContent="space-between" sx={{ borderBottom: '1px solid #000', paddingBottom: '7px', marginBottom: '14px' }}>
                <Grid item sx={{ display: 'flex' }}>
                    <Typography className='onlineOrderTitleFont' sx={{ fontWeight: '800' }}>
                        #{order?.order?.order_sequence}{order?.order?.order_no}
                    </Typography>
                    {shouldShowNewTag && (
                        <Box component="span" sx={{ backgroundColor: '#FF6347', color: '#fff', fontSize: '10px', padding: '4px 15px 4px 15px', borderRadius: '12px', marginLeft: '10px' }}>
                            New
                        </Box>
                    )}
                    {order?.order?.kioskUser && (
                        <Box component="span" sx={{ backgroundColor: '#F77F00', color: '#fff', fontSize: '10px', fontWeight: '800', padding: '4px 15px 4px 15px', borderRadius: '12px', marginLeft: '10px' }}>
                            KIOSK
                        </Box>
                    )}
                    {order?.order?.orderStatus === "READY-TO-PICK" &&
                        <Box component="span" sx={{ backgroundColor: '#6A0572', color: '#fff', fontSize: '10px', fontWeight: '800', padding: '4px 15px 4px 15px', borderRadius: '12px', marginLeft: '10px' }}>
                            Rider Assigned
                        </Box>
                    }
                </Grid>
                <Grid item>
                    <Typography className='onlineOrderFont' sx={{ fontWeight: '800' }}>{order?.order?.orderItemsCount} Item(s)</Typography>
                </Grid>
                <Grid item>
                    <Typography className='onlineOrderTitleFont' sx={{ fontWeight: '800' }}>
                        {/* {order?.order?.ordersType === 'PICKUP' ? order?.order?.readyTime : order?.order?.readyTime} */}
                        {getReducedReadyTime(order?.order?.readyTime)}
                    </Typography>
                </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
                <Grid item md={8}>
                    <Grid container sx={{ padding: '4px 0 4px 0' }}>
                        <Grid item md={3}>
                            <Typography className="onlineOrderFont opacityText" sx={{ fontWeight: '600', padding: '2px' }} >
                                {order?.order?.ordersType}
                            </Typography>
                        </Grid>
                        <Grid item md={9} sx={{ textAlign: 'right' }}>
                            <Typography className='onlineOrderTitleFont' sx={{ fontWeight: '600' }}>{order?.order?.orderStatus}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item md={4} sx={{ textAlign: 'right' }}>
                    <Typography className='onlineOrderTitleFont' sx={{ fontWeight: '800', color: 'green' }}>
                        ₹ {formattedGrandTotal}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

const ExpandedView = ({ order }) => {
    const formattedGrandTotal = order?.order?.grandTotal?.toFixed(2);
    const dateString = order?.order?.createdAt;
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;

    return (
        <Grow in={true} timeout={300} appear>
            <Box className="cursor-p" sx={{ backgroundColor: '#003049', color: '#fff', padding: '13px 17px 13px 17px', margin: '10px', marginTop: '18px', borderRadius: '8px' }}>
                <Grid container justifyContent="space-between" sx={{ borderBottom: '1px solid #fff', paddingBottom: '7px', marginBottom: '14px' }}>
                    <Grid item>
                        <Typography className='onlineOrderTitleFont' sx={{ fontWeight: '800' }}>
                            Order #{order?.order?.order_sequence}{order?.order?.order_no}
                        </Typography>
                    </Grid>
                    <Grid item>
                        {order?.order?.kioskUser && (
                            <Box component="span" sx={{ backgroundColor: '#A0D2DB', color: '#fff', fontSize: '14px', fontWeight: '800', padding: '5px 25px 4px 25px', borderRadius: '50px', marginTop: '-14px' }}>
                                KIOSK
                            </Box>
                        )}
                    </Grid>
                    <Grid item>
                        <Typography className='onlineOrderTitleFont' sx={{ fontWeight: '800' }}>
                            <span style={{ padding: '0 10px 0 0' }}>{order?.order?.ordersType === 'DELIVERY' ? 'Ready Time' : 'Pick Up Time'}</span>
                            {/* {order?.order?.ordersType === 'PICKUP' ? order?.order?.readyTime : order?.order?.readyTime} */}
                            {getReducedReadyTime(order?.order?.readyTime)}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container justifyContent="space-between">
                    <Grid item md={8}>
                        <Grid container sx={{ padding: '4px 0 4px 0' }}>
                            <Grid item md={6} sx={{ display: 'flex' }} >
                                <Typography className="onlineOrderFont opacityText" >
                                    Products
                                </Typography>
                                <Typography sx={{ paddingLeft: '20px', fontWeight: '800' }} className='onlineOrderFont'>
                                    {order?.orderItems?.length}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Typography className="onlineOrderFont opacityText" >
                                    Quantity
                                </Typography>
                                <Typography sx={{ paddingLeft: '20px', fontWeight: '800' }} className='onlineOrderFont'>
                                    {order?.order?.orderItemsCount}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container sx={{ padding: '4px 0 4px 0' }}>
                            <Grid item md={7}>
                                <Typography className='onlineOrderFont  opacityText'>
                                    Order Type
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography className='onlineOrderFont' sx={{ fontWeight: '800' }}>
                                    {order?.order?.ordersType}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container sx={{ padding: '4px 0 4px 0' }}>
                            <Grid item md={7}>
                                <Typography className='onlineOrderFont opacityText'>
                                    Order Time
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography className='onlineOrderFont' sx={{ fontWeight: '800' }}>
                                    {formattedTime}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item md={4}>
                        {/* <Grid container sx={{  }}> */}
                        <Typography className='onlineOrderTitleFont' sx={{ marginTop: '60px', fontWeight: '800', textAlign: 'right' }}>
                            ₹ {formattedGrandTotal}
                        </Typography>
                        {/* <Box className='onlineOrderTitleFont' sx={{ backgroundColor: order?.order?.paymentStatus === 'PAID' ? '#36CD1D' : '#FB9000', color: '#003049', padding: '1px 20px 1px 20px', borderRadius: '30px', marginLeft: '10px' }}>
                            {order?.order?.paymentStatus === 'PAID' ? 'Paid' : 'Not Paid'}                    
                        </Box> */}
                        {/* </Grid> */}
                    </Grid>
                </Grid>
            </Box>
        </Grow>
    );
};

const DelayedOrderView = ({ order }) => {
    const TOTAL_SECONDS = Math.max(1, Number(API.BUFFER_TIME || 0) * 60);
    const [now, setNow] = useState(Date.now());
    const hasPlayedSound = useRef(false);

    const orderId = order?.order?._id;
    const delayStart = getOrderDelayStartTime(order);

    useEffect(() => {
        const timer = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!delayStart || !orderId) return;

        const elapsedSinceDelay = Math.floor((now - delayStart.getTime()) / 1000);

        if (elapsedSinceDelay >= 0 && !hasPlayedSound.current) {
            const playedOrders = JSON.parse(localStorage.getItem('playedDelayedSoundOrders') || '[]');

            if (!playedOrders.includes(orderId)) {
                playSound();
                localStorage.setItem(
                    'playedDelayedSoundOrders',
                    JSON.stringify([...playedOrders, orderId])
                );
            }

            hasPlayedSound.current = true;
        }
    }, [now, delayStart, orderId]);

    if (!delayStart) return null;

    const elapsedSinceDelay = Math.max(
        0,
        Math.floor((now - delayStart.getTime()) / 1000)
    );

    const isOvertime = elapsedSinceDelay >= TOTAL_SECONDS;
    const secondsLeft = isOvertime
        ? elapsedSinceDelay - TOTAL_SECONDS
        : TOTAL_SECONDS - elapsedSinceDelay;

    const isUrgent = !isOvertime && secondsLeft <= TOTAL_SECONDS * 0.20;

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${isOvertime ? '+' : ''}${m}:${s.toString().padStart(2, '0')}`;
    };

    const progress = isOvertime ? 100 : (secondsLeft / TOTAL_SECONDS) * 100;

    const statusLabel = isOvertime
        ? "DELAYED"
        : isUrgent
            ? "DELAYING SOON"
            : "DELAY TIMER";

    const statusMessage = isOvertime
        ? "Order is now delayed"
        : isUrgent
            ? "Order will be delayed soon"
            : "Delay buffer is running";

    const subMessage = isOvertime
        ? "Please take action immediately"
        : isUrgent
            ? "Few seconds left before it becomes delayed"
            : "Order is still within delay buffer";

    return (
        <Box
            className="cursor-p"
            sx={{
                position: "relative",
                margin: "10px",
                marginTop: "18px",
                borderRadius: "12px",
                overflow: "hidden",
                cursor: "pointer",
                background: isOvertime
                    ? `
                        radial-gradient(circle at 85% 0%, rgba(255,23,68,0.25), transparent 42%),
                        radial-gradient(circle at 0% 100%, rgba(255,82,82,0.18), transparent 48%),
                        linear-gradient(145deg, #2B1118, #471A24)
                    `
                    : isUrgent
                        ? `
                            radial-gradient(circle at 85% 0%, rgba(255,193,7,0.18), transparent 42%),
                            radial-gradient(circle at 0% 100%, rgba(255,138,128,0.12), transparent 48%),
                            linear-gradient(145deg, #272016, #3A2B18)
                        `
                        : `
                            radial-gradient(circle at 85% 0%, rgba(255,82,82,0.14), transparent 42%),
                            radial-gradient(circle at 0% 100%, rgba(255,138,128,0.08), transparent 48%),
                            linear-gradient(145deg, #1A1A28, #2A2A3D)
                        `,
                color: "#fff",
                boxShadow: isOvertime
                    ? "0 8px 22px rgba(255,23,68,0.32), inset 0 1px 0 rgba(255,255,255,0.05)"
                    : "0 6px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                animation: isOvertime
                    ? "overtimePulse 1.2s ease-in-out infinite"
                    : isUrgent
                        ? "cardUrgentFloat 1.2s ease-in-out infinite"
                        : "cardSoftFloat 3.8s ease-in-out infinite",

                "@keyframes cardSoftFloat": {
                    "0%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-2px)" },
                    "100%": { transform: "translateY(0px)" },
                },

                "@keyframes cardUrgentFloat": {
                    "0%": { transform: "translateY(0px) scale(1)" },
                    "50%": { transform: "translateY(-1px) scale(1.006)" },
                    "100%": { transform: "translateY(0px) scale(1)" },
                },

                "@keyframes overtimePulse": {
                    "0%": { transform: "scale(1)", boxShadow: "0 8px 22px rgba(255,23,68,0.25)" },
                    "50%": { transform: "scale(1.008)", boxShadow: "0 10px 28px rgba(255,23,68,0.48)" },
                    "100%": { transform: "scale(1)", boxShadow: "0 8px 22px rgba(255,23,68,0.25)" },
                },

                "@keyframes shimmerMove": {
                    "0%": { transform: "translateX(-120%)" },
                    "100%": { transform: "translateX(120%)" },
                },

                "@keyframes pulseRing": {
                    "0%": { transform: "scale(1)", opacity: 0.35 },
                    "70%": { transform: "scale(1.15)", opacity: 0 },
                    "100%": { transform: "scale(1.15)", opacity: 0 },
                },

                "@keyframes glowBar": {
                    "0%": { opacity: 0.45 },
                    "50%": { opacity: 1 },
                    "100%": { opacity: 0.45 },
                },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "35%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
                    transform: "translateX(-120%)",
                    animation: "shimmerMove 6s linear infinite",
                    opacity: isOvertime ? 0.7 : 0.4,
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    height: isOvertime ? "4px" : "3px",
                    width: isOvertime ? "100%" : `${progress}%`,
                    background: isOvertime
                        ? "linear-gradient(90deg, #FF1744, #FF5252, #FF1744)"
                        : isUrgent
                            ? "linear-gradient(90deg, #FF9800, #FFD54F)"
                            : "linear-gradient(90deg, #FF5252, #FFD54F)",
                    boxShadow: isOvertime
                        ? "0 0 14px rgba(255,23,68,0.85)"
                        : isUrgent
                            ? "0 0 10px rgba(255,193,7,0.75)"
                            : "0 0 8px rgba(255,138,128,0.4)",
                    transition: "width 1s linear",
                    animation: "glowBar 1.5s ease-in-out infinite",
                }}
            />

            <Box sx={{ padding: "12px 16px" }}>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                        paddingBottom: "7px",
                        marginBottom: "10px",
                    }}
                >
                    <Grid item sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Typography sx={{ fontWeight: 800, fontSize: "14px", letterSpacing: "0.3px" }}>
                            #{order?.order?.order_sequence}{order?.order?.order_no}
                        </Typography>

                        <Box
                            component="span"
                            sx={{
                                backgroundColor: isOvertime
                                    ? "rgba(255,23,68,0.95)"
                                    : isUrgent
                                        ? "rgba(255,193,7,0.22)"
                                        : "rgba(255,82,82,0.16)",
                                color: isOvertime
                                    ? "#fff"
                                    : isUrgent
                                        ? "#FFD54F"
                                        : "#FF8A80",
                                fontSize: "10px",
                                fontWeight: 900,
                                padding: "4px 12px",
                                borderRadius: "12px",
                                lineHeight: 1.2,
                                letterSpacing: "0.3px",
                            }}
                        >
                            {statusLabel}
                        </Box>
                    </Grid>

                    <Grid item>
                        <Typography sx={{ fontWeight: 800, fontSize: "13px" }}>
                            {order?.order?.orderItemsCount} Item(s)
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Box
                            sx={{
                                position: "relative",
                                width: 54,
                                height: 54,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    width: 54,
                                    height: 54,
                                    borderRadius: "50%",
                                    border: isOvertime
                                        ? "2px solid rgba(255,23,68,0.35)"
                                        : "2px solid rgba(255,138,128,0.18)",
                                    animation: isOvertime || isUrgent
                                        ? "pulseRing 1s linear infinite"
                                        : "pulseRing 2.2s linear infinite",
                                }}
                            />

                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "50%",
                                    background: `conic-gradient(${
                                        isOvertime ? "#FF1744" : isUrgent ? "#FF9800" : "#FFD54F"
                                    } ${progress * 3.6}deg, rgba(255,255,255,0.08) 0deg)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: isOvertime
                                        ? "0 0 16px rgba(255,23,68,0.45)"
                                        : isUrgent
                                            ? "0 0 14px rgba(255,152,0,0.3)"
                                            : "0 0 10px rgba(255,213,79,0.18)",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "50%",
                                        background: "rgba(19,19,31,0.96)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "10px",
                                            fontWeight: 900,
                                            lineHeight: 1,
                                            color: isOvertime
                                                ? "#FF5252"
                                                : isUrgent
                                                    ? "#FFD54F"
                                                    : "#fff",
                                        }}
                                    >
                                        {formatTime(secondsLeft)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item md={8}>
                        <Typography sx={{ fontWeight: 800, fontSize: "13px" }}>
                            {statusMessage}
                        </Typography>

                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: "11px",
                                opacity: 0.75,
                                marginTop: "2px",
                            }}
                        >
                            {subMessage}
                        </Typography>
                    </Grid>

                    <Grid item md={4} sx={{ textAlign: "right" }}>
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: "13px",
                                color: isOvertime ? "#FF8A80" : "#FFD54F",
                            }}
                        >
                            ₹ {order?.order?.grandTotal?.toFixed(2)}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default OrderCard;